import { action, get } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { endOfDay, fromUnixTime, getUnixTime, startOfDay } from 'date-fns';
import { task } from 'ember-concurrency';
import type EmployeeModel from 'garaje/models/employee';
import type ReservationModel from 'garaje/models/reservation';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { AvailabilityStatus, ResourceType } from 'garaje/services/live-map';
import type LiveMapService from 'garaje/services/live-map';
import type RegistrationsService from 'garaje/services/registrations';
import type ScheduleGraphqlService from 'garaje/services/schedule-graphql';
import type StateService from 'garaje/services/state';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { DEFAULT_TIMEZONE, modifyDateInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import type { EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import {
  buildGroupReservationInitialDate,
  buildGroupReservationInterval,
  buildGroupReservationMinDate,
  formatGroupReservationDate,
} from 'garaje/utils/group-reservation-timestamps';

type Phase = 'collaborators' | 'desk-selection';

interface GroupDeskReservationModalArgs {
  isOpen: boolean;
  onClose: () => void;
  date: number | Date;
}

export default class GroupDeskReservationModalComponent extends Component<GroupDeskReservationModalArgs> {
  @service declare state: StateService;
  @service declare store: Store;
  @service declare scheduleGraphql: ScheduleGraphqlService;
  @service declare router: RouterService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare flashMessages: FlashMessagesService;
  @service declare liveMap: LiveMapService;
  @service declare registrations: RegistrationsService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  readonly MAX_COLLABORATORS = 10;

  // prettier-ignore
  @(employeesSearcherTask({
    filter: { deleted: false, excludeHidden: true },
    prefix: true,
    include: 'user',
  }).restartable())
  searchEmployeesTask!: EmployeeSearcherTask;

  @tracked currentPhase: Phase = 'collaborators';
  @tracked selectedCollaborators: EmployeeModel[] = [];
  @tracked selectedDate: Date = this.initialSelectedDate;
  @tracked searchTerm = '';
  @tracked isSearchExpanded = false;
  @tracked showSearchResults = false;
  @tracked highlightedIndex = -1;
  @tracked isLoadingCurrentEmployee = false;
  @tracked employeesWithExistingReservations: EmployeeModel[] = [];
  @tracked hasEnoughDesks = true;
  @tracked availableDesks: unknown[] = [];
  @tracked selectedDesks: unknown[] = [];
  @tracked isBookingDesks = false;
  @tracked deskAvailabilityMap: Map<string, AvailabilityStatus> = new Map();
  @tracked hasUserManuallyEmptiedSelection = false;

  get initialSelectedDate(): Date {
    const timezone = this.locationTimezone;
    const rawArgsDate = this.args.date;
    let argsDate: Date | undefined;
    if (typeof rawArgsDate === 'number') {
      argsDate = fromUnixTime(rawArgsDate);
    } else if (rawArgsDate) {
      argsDate = rawArgsDate;
    }
    return buildGroupReservationInitialDate(argsDate, new Date(), timezone);
  }

  get isCollaboratorsPhase(): boolean {
    return this.currentPhase === 'collaborators';
  }

  get isDeskSelectionPhase(): boolean {
    return this.currentPhase === 'desk-selection';
  }

  get formattedSelectedDate(): string {
    return formatGroupReservationDate(this.selectedDate, this.locationTimezone);
  }

  get minBookableDate(): Date {
    return buildGroupReservationMinDate(new Date(), this.locationTimezone);
  }

  get locationTimezone(): string {
    return this.state.currentLocation?.timezone || DEFAULT_TIMEZONE;
  }

  get floorsWithAvailableDesks(): unknown[] {
    if (!this.availableDesks || this.availableDesks.length === 0) {
      return [];
    }

    // Extract unique floors from available desks
    const floorMap = new Map<unknown, unknown>();
    for (const desk of this.availableDesks) {
      // eslint-disable-next-line ember/no-get
      const floor = get(desk, 'floor');
      // eslint-disable-next-line ember/no-get
      const floorId = get(floor, 'id');
      if (floorId && !floorMap.has(floorId)) {
        // eslint-disable-next-line ember/use-ember-get-and-set
        floorMap.set(floorId, floor);
      }
    }

    return Array.from(floorMap.values());
  }

  get selectedDeskCount(): number {
    return this.selectedDesks.length;
  }

  get canConfirmSelection(): boolean {
    return this.selectedDesks.length === this.selectedCollaborators.length && this.selectedDesks.length > 0;
  }

  get canProceedToNextPhase(): boolean {
    return (
      this.selectedCollaborators.length > 0 &&
      this.selectedCollaborators.length <= this.MAX_COLLABORATORS &&
      this.hasEnoughDesks &&
      !this.checkDeskAvailabilityTask.isRunning
    );
  }

  get isMaxCollaboratorsReached(): boolean {
    return this.selectedCollaborators.length >= this.MAX_COLLABORATORS;
  }

  checkDeskAvailabilityTask = task({ restartable: true }, async () => {
    if (this.selectedCollaborators.length === 0 || !this.selectedDate) {
      this.hasEnoughDesks = true;
      this.availableDesks = [];
      return;
    }

    const requiredDesks = this.selectedCollaborators.length;

    try {
      // Fetch LiveMap data directly without polling
      const selectedTime = getUnixTime(this.selectedDate);
      const liveMapPayload = await this.liveMap.fetchLiveMapDataTask.perform({
        locationId: this.state.currentLocation?.id,
        selectedTime,
      });

      // Build a Map of desk availability from the live map data and store for Phase 2
      this.deskAvailabilityMap = new Map<string, AvailabilityStatus>();
      for (const resourceState of liveMapPayload.data.live) {
        if (resourceState.resourceType === ResourceType.DESK) {
          // eslint-disable-next-line ember/use-ember-get-and-set
          this.deskAvailabilityMap.set(resourceState.resourceId, resourceState.status.availabilityStatus);
        }
      }

      // Use store.query to get proper Ember Data models with computed properties
      const params = {
        include: 'location,floor,company',
        filter: {
          'location-id': this.state.currentLocation?.id,
          enabled: true,
        },
      };

      const desks = await this.store.query('desk', params);

      // First filter: static properties (enabled, placed, not deleted)
      const staticFiltered = desks.filter((desk) => {
        const enabled = desk.enabled;
        const placed = desk.placed;
        const isDeleted = desk.isDeleted;
        return enabled && placed && !isDeleted;
      });

      // Second filter: LiveMap real-time availability using stored map
      const availableDesks = staticFiltered.filter((desk) => {
        const deskId = desk.id;
        // eslint-disable-next-line ember/use-ember-get-and-set
        const availabilityStatus = this.deskAvailabilityMap.get(deskId);
        // Only show desks that are explicitly AVAILABLE (not booked)
        return availabilityStatus === AvailabilityStatus.AVAILABLE;
      });

      // Store available desks for Phase 2
      this.availableDesks = availableDesks;

      // Group desks by floor.id and count desks per floor
      const desksByFloor = new Map<string, number>();
      for (const desk of availableDesks) {
        // eslint-disable-next-line ember/no-get
        const floorId = get(desk, 'floor.id') as string | undefined;
        if (floorId && typeof floorId === 'string') {
          // eslint-disable-next-line ember/use-ember-get-and-set
          const currentCount = desksByFloor.get(floorId) || 0;
          // eslint-disable-next-line ember/use-ember-get-and-set
          desksByFloor.set(floorId, currentCount + 1);
        }
      }

      // Check if any floor has enough desks
      const hasEnough = Array.from(desksByFloor.values()).some((count) => count >= requiredDesks);
      this.hasEnoughDesks = hasEnough;
    } catch {
      // Block the user if there's an exception - we can't verify desk availability
      this.hasEnoughDesks = false;
      this.availableDesks = [];
    }
  });

  checkExistingReservationsTask = task({ restartable: true }, async () => {
    if (this.selectedCollaborators.length === 0 || !this.selectedDate) {
      this.employeesWithExistingReservations = [];
      return;
    }

    const interval = buildGroupReservationInterval(this.selectedDate, new Date(), this.locationTimezone);
    if (interval.wasClamped) {
      this.selectedDate = interval.effectiveSelectedDate;
    }
    const startDate = getUnixTime(new Date(interval.startISO));
    const endDate = getUnixTime(new Date(interval.endISO));

    const employeesWithReservations: EmployeeModel[] = [];

    for (const employee of this.selectedCollaborators) {
      // eslint-disable-next-line ember/no-get
      const userId = get(employee, 'user.id');
      if (!userId) {
        continue;
      }

      try {
        const reservations = await this.store.query('reservation', {
          filter: {
            'location-id': this.state.currentLocation?.id,
            'start-date': startDate,
            'end-date': endDate,
            'user-id': userId,
          },
        });

        const reservationsArray = reservations.slice();
        const hasActiveReservation = reservationsArray.some(
          (res: ReservationModel) => !res.canceledAt || res.checkInTime,
        );

        if (hasActiveReservation) {
          employeesWithReservations.push(employee);
        }
      } catch {
        // Silently fail - don't block the UI if reservation check fails
      }
    }

    this.employeesWithExistingReservations = employeesWithReservations;
  });

  get collaboratorsWithExistingReservations(): EmployeeModel[] {
    return this.employeesWithExistingReservations;
  }

  get hasExistingReservations(): boolean {
    return this.collaboratorsWithExistingReservations.length > 0;
  }

  get existingReservationsCount(): number {
    return this.collaboratorsWithExistingReservations.length;
  }

  get existingReservationsWarningText(): string {
    const count = this.existingReservationsCount;
    if (count === 0) return '';

    const firstEmployee = this.collaboratorsWithExistingReservations[0];
    if (!firstEmployee) return '';

    if (count === 1) {
      return `${firstEmployee.name} has an existing desk reservation`;
    } else {
      const otherCount = count - 1;
      return `${firstEmployee.name} and ${otherCount} other${
        otherCount > 1 ? 's' : ''
      } have existing desk reservations`;
    }
  }

  get existingReservationsSubtext(): string {
    const count = this.existingReservationsCount;
    if (count === 0) return '';

    const firstEmployee = this.collaboratorsWithExistingReservations[0];
    if (!firstEmployee) return '';

    if (count === 1) {
      const firstName = firstEmployee.name.split(' ')[0];
      return `Including ${firstName} will replace their current desk reservation`;
    } else {
      return `Including them will replace their current desk reservations`;
    }
  }

  get reservationLogQueryParams(): { startDate: Date; endDate: Date; selectedDateRange: string } {
    const timezone = this.locationTimezone;
    const startDate = modifyDateInTimeZone(this.selectedDate, timezone, startOfDay);
    const endDate = modifyDateInTimeZone(this.selectedDate, timezone, endOfDay);

    return {
      startDate,
      endDate,
      selectedDateRange: 'Custom range',
    };
  }

  get searchResults(): EmployeeModel[] {
    if (!this.searchEmployeesTask.lastSuccessful) {
      return [];
    }

    const results = this.searchEmployeesTask.lastSuccessful.value || [];
    // Filter out already selected collaborators
    return results.filter((employee) => !this.selectedCollaborators.some((selected) => selected.id === employee.id));
  }

  get listboxId(): string {
    return `${guidFor(this)}-collaboration-listbox`;
  }

  get activeOptionId(): string | null {
    if (this.highlightedIndex < 0 || this.highlightedIndex >= this.searchResults.length) {
      return null;
    }
    return this.optionId(this.highlightedIndex);
  }

  @action
  optionId(index: number): string {
    return `${guidFor(this)}-collaboration-option-${index}`;
  }

  @action
  isCurrentUser(employee: EmployeeModel): boolean {
    // eslint-disable-next-line ember/no-get
    const employeeEmail = get(employee, 'user.email');
    return employeeEmail === this.state.currentUser.email;
  }

  @action
  goToNextPhase(): void {
    if (this.isCollaboratorsPhase && this.canProceedToNextPhase) {
      this.currentPhase = 'desk-selection';
    }
  }

  @action
  goToPreviousPhase(): void {
    if (this.isDeskSelectionPhase) {
      this.currentPhase = 'collaborators';
    }
  }

  @action
  handleClose(): void {
    // Prevent closing while booking is in progress
    if (this.isBookingDesks) {
      return;
    }

    // Reset all state when closing to ensure fresh modal on next open
    this.currentPhase = 'collaborators';
    this.selectedCollaborators = [];
    this.selectedDate = this.initialSelectedDate;
    this.searchTerm = '';
    this.isSearchExpanded = false;
    this.showSearchResults = false;
    this.highlightedIndex = -1;
    this.employeesWithExistingReservations = [];
    this.hasEnoughDesks = true;
    this.availableDesks = [];
    this.selectedDesks = [];
    this.deskAvailabilityMap = new Map();
    this.hasUserManuallyEmptiedSelection = false;
    this.args.onClose();
  }

  @action
  updateDate(date: Date | null): void {
    if (date) {
      this.selectedDate = date;
      this.selectedDesks = [];
      void this.checkExistingReservationsTask.perform();
      void this.checkDeskAvailabilityTask.perform();

      // Blur the datepicker input after date selection
      // Use next() to defer blur until after rome calendar's focus handling
      next(() => {
        const datepickerInput = document.querySelector('[data-test-date-picker] input') as HTMLInputElement;
        if (datepickerInput) {
          datepickerInput.blur();
        }
      });
    }
  }

  @action
  toggleDeskSelection(desk: unknown): void {
    // eslint-disable-next-line ember/no-get
    const deskId = get(desk, 'id');
    // eslint-disable-next-line ember/no-get
    const isAlreadySelected = this.selectedDesks.some((d) => get(d, 'id') === deskId);

    if (isAlreadySelected) {
      // Remove from selection (deselect)
      // eslint-disable-next-line ember/no-get
      this.selectedDesks = this.selectedDesks.filter((d) => get(d, 'id') !== deskId);
    } else if (this.selectedDesks.length < this.selectedCollaborators.length) {
      // Add to selection (only if we haven't hit the limit)
      this.selectedDesks = [...this.selectedDesks, desk];
    }
  }

  @action
  clearDeskSelection(): void {
    this.selectedDesks = [];
  }

  @action
  toggleSearch(): void {
    this.isSearchExpanded = !this.isSearchExpanded;
    if (!this.isSearchExpanded) {
      this.searchTerm = '';
      this.showSearchResults = false;
      this.highlightedIndex = -1;
    }
  }

  @action
  focusSearchInput(element: HTMLInputElement): void {
    element.focus();
  }

  @action
  handleSearchInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.searchTerm = term;
    this.showSearchResults = term.trim().length > 0;
    this.highlightedIndex = -1;

    // Perform the search task if there's a search term
    if (term.trim().length > 0) {
      void this.searchEmployeesTask.perform(term);
    }
  }

  @action
  clearSearch(): void {
    this.searchTerm = '';
    this.showSearchResults = false;
    this.highlightedIndex = -1;
  }

  @action
  handleSearchFocusOut(): void {
    setTimeout(() => {
      this.showSearchResults = false;
      this.highlightedIndex = -1;
    }, 200);
  }

  @action
  handleSearchKeydown(event: KeyboardEvent): void {
    const results = this.searchResults;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.showSearchResults || results.length === 0) return;
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, results.length - 1);
        this.scrollHighlightedOptionIntoView();
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (!this.showSearchResults || results.length === 0) return;
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        this.scrollHighlightedOptionIntoView();
        break;

      case 'Home':
        if (!this.showSearchResults || results.length === 0) return;
        event.preventDefault();
        this.highlightedIndex = 0;
        this.scrollHighlightedOptionIntoView();
        break;

      case 'End':
        if (!this.showSearchResults || results.length === 0) return;
        event.preventDefault();
        this.highlightedIndex = results.length - 1;
        this.scrollHighlightedOptionIntoView();
        break;

      case 'Enter':
        if (!this.showSearchResults) return;
        if (this.highlightedIndex < 0 || this.highlightedIndex >= results.length) return;
        event.preventDefault();
        this.addCollaborator(results[this.highlightedIndex]!);
        break;

      case 'Escape':
        if (!this.showSearchResults && !this.searchTerm) return;
        event.preventDefault();
        this.clearSearch();
        break;
    }
  }

  @action
  setHighlightedIndex(index: number): void {
    this.highlightedIndex = index;
  }

  private scrollHighlightedOptionIntoView(): void {
    const id = this.activeOptionId;
    if (!id) return;
    next(this, () => {
      document.getElementById(id)?.scrollIntoView({ block: 'nearest' });
    });
  }

  @action
  addCollaborator(employee: EmployeeModel): void {
    if (this.isMaxCollaboratorsReached) {
      return;
    }

    const isAlreadySelected = this.selectedCollaborators.some((emp) => emp.id === employee.id);
    if (!isAlreadySelected) {
      this.hasUserManuallyEmptiedSelection = false;
      this.selectedCollaborators = [...this.selectedCollaborators, employee];
      void this.checkExistingReservationsTask.perform();
      void this.checkDeskAvailabilityTask.perform();
    }
    this.searchTerm = '';
    this.showSearchResults = false;
    this.highlightedIndex = -1;
  }

  @action
  removeCollaborator(employee: EmployeeModel, event: Event): void {
    event.stopPropagation();
    const willBeEmpty = this.selectedCollaborators.length === 1;
    this.selectedCollaborators = this.selectedCollaborators.filter((emp) => emp.id !== employee.id);
    if (willBeEmpty) {
      this.hasUserManuallyEmptiedSelection = true;
    }
    void this.checkExistingReservationsTask.perform();
    void this.checkDeskAvailabilityTask.perform();
  }

  fetchCurrentEmployeeTask = task({}, async () => {
    const employees = await this.store.query('employee', {
      filter: {
        locations: this.state.currentLocation?.id,
        'email-in': this.state.currentUser.email,
        deleted: false,
      },
      include: 'user',
    });
    return employees.slice()[0];
  });

  @action
  async loadCurrentEmployee(): Promise<void> {
    if (this.selectedCollaborators.length === 0 && !this.hasUserManuallyEmptiedSelection) {
      this.isLoadingCurrentEmployee = true;
      try {
        const currentEmployee = await this.fetchCurrentEmployeeTask.perform();
        if (currentEmployee) {
          this.selectedCollaborators = [currentEmployee];
          void this.checkExistingReservationsTask.perform();
          void this.checkDeskAvailabilityTask.perform();
        }
      } finally {
        this.isLoadingCurrentEmployee = false;
      }
    }
  }

  @action
  async confirmReservation(): Promise<void> {
    this.isBookingDesks = true;
    try {
      // Compute the interval once. If the modal sat open across office-TZ midnight,
      // buildGroupReservationInterval will clamp selectedDate up to the new minimum
      // and flag it — surface a warning and abort so the user can re-confirm.
      const interval = buildGroupReservationInterval(this.selectedDate, new Date(), this.locationTimezone);
      if (interval.wasClamped) {
        this.selectedDate = interval.effectiveSelectedDate;
        this.flashMessages.showAndHideFlash(
          'warning',
          'Your selected date is no longer in the future. We updated it to the next available day — please confirm again.',
        );
        this.isBookingDesks = false;
        return;
      }
      const startDate = interval.startISO;
      const endDate = interval.endISO;

      // Get current user's email for reservedBy field
      const reservedBy = this.state.currentUser.email;

      const data = {
        reservationRequests: this.selectedCollaborators.map((collaborator, index) => {
          // collaborator 1 gets desk 1, collaborator 2 gets desk 2, etc.
          const desk = this.selectedDesks[index];
          // eslint-disable-next-line ember/no-get
          const deskId = get(desk, 'id');
          // eslint-disable-next-line ember/no-get
          const userId = get(collaborator, 'user.id');
          return {
            userId,
            locationId: this.state.currentLocation?.id,
            deskId,
            startDate,
            endDate,
            bookingSource: 'DASHBOARD',
            reservationScheme: 'GROUP',
            reservedBy,
          };
        }),
      };

      // Call RMS v2 group reservations endpoint
      await this.ajaxFetch.request('/a/rms/v2/group-reservations', {
        type: 'POST',
        contentType: 'application/json',
        data,
      });

      // Track success metrics
      const collaboratorCount = this.selectedCollaborators.length;
      this.workplaceMetrics.trackEvent('GROUP_RESERVATION_CREATED', {
        collaborators_count: collaboratorCount,
        date: this.selectedDate.toISOString(),
        locationId: this.state.currentLocation?.id,
      });

      // Navigate to floor map with selected desks
      // eslint-disable-next-line ember/no-get
      const floorId = get(this.selectedDesks[0], 'floor.id') as string;
      const selectedTime = getUnixTime(new Date(startDate));

      // Redirect to live map view with success info
      // Keep loading state active during navigation
      void this.router.transitionTo('spaces.maps.live.show', floorId, {
        queryParams: {
          selectedTime,
          selectedResourceFilter: 'desks',
          groupDeskReservationCount: collaboratorCount,
        },
      });
    } catch (error) {
      // On error: clear loading state, close modal, and show error message
      this.isBookingDesks = false;
      this.handleReservationError(error);
      this.handleClose();
    }
  }

  /**
   * Handles errors from the group reservation API call
   * Logs to monitoring and displays error message to user
   * @param error - The error from the API call
   */
  private handleReservationError(error: unknown): void {
    // Log to monitoring
    this.workplaceMetrics.logMonitorError({
      event: 'ERROR_CREATING_GROUP_RESERVATION',
      error: error as Error,
      debugExtras: {
        collaborators_count: this.selectedCollaborators.length,
        date: this.selectedDate,
      },
    });

    // Parse and display error to user
    const errorMessage = parseErrorForDisplay(error) || 'Failed to create group reservation';
    this.flashMessages.showAndHideFlash('error', errorMessage);
  }
}
